import LockIcon from "@mui/icons-material/Lock";
import { alpha, Box, Typography } from "@mui/material";
import { defaultShadowsForSignals } from "components/Signals/SignalsUtils";
import { useCardLayout } from "./CardLayoutContext";
import { CARD_RADIUS } from "./grid";
import { LayoutGrip } from "./LayoutGrip";

/**
 * Holds the place of a card the viewer cannot see, so the cards around it sit
 * where everyone else sees them. The server sends only the card's id and
 * place, so the header names no card. While arranging it can be moved but not
 * resized, and it has no actions, so it cannot be deleted.
 */
export const HiddenCardPlaceholder = ({ objectId }: { objectId: string }) => {
    const canArrange = useCardLayout()?.canArrange ?? false;

    return (
        <Box
            id={objectId}
            sx={(theme) => ({
                position: "relative",
                display: "flex",
                flexDirection: "column",
                width: "100%",
                height: "100%",
                overflow: "hidden",
                boxSizing: "border-box",
                borderRadius: CARD_RADIUS,
                boxShadow: defaultShadowsForSignals,
                backgroundColor: theme.palette.gray[50],
                backgroundImage: `repeating-linear-gradient(-45deg, ${theme.palette.gray[100]} 0 8px, transparent 8px 20px)`,
                // The same dashed outline every card shows while arranging.
                ...(canArrange && {
                    boxShadow: "none",
                    border: `1px dashed ${alpha(
                        theme.palette.primary.main,
                        0.4,
                    )}`,
                }),
            })}
        >
            <Box
                sx={(theme) => ({
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    flex: "0 0 auto",
                    paddingInline: theme.spacing(3),
                    paddingBlock: theme.spacing(2),
                })}
            >
                <LockIcon
                    aria-hidden
                    sx={(theme) => ({
                        fontSize: 18,
                        color: theme.palette.gray[400],
                    })}
                />
                <Typography
                    variant="h2"
                    noWrap
                    sx={(theme) => ({ color: theme.palette.gray[700] })}
                >
                    Restricted card
                </Typography>
            </Box>
            <Box
                sx={(theme) => ({
                    display: "flex",
                    flex: "1 1 auto",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 0.5,
                    minHeight: 0,
                    padding: theme.spacing(0, 3, 2),
                    textAlign: "center",
                    color: theme.palette.gray[400],
                })}
            >
                <Typography sx={{ fontWeight: 600, color: "inherit" }}>
                    You don't have access to this card
                </Typography>
                {canArrange && (
                    <Typography variant="body2" sx={{ color: "inherit" }}>
                        You can still change its position in the layout.
                    </Typography>
                )}
            </Box>
            <LayoutGrip
                objectId={objectId}
                label="Move a card you don't have access to"
                tooltip="Drag to move. Arrow keys move. A card you don't have access to can't be resized."
            />
        </Box>
    );
};

export default HiddenCardPlaceholder;
